/**
 * normalizeProfile.js
 * 
 * SINGLE SOURCE OF TRUTH for profile data normalization.
 * 
 * Problem this solves:
 * Backend can return profiles with different field names depending on endpoint:
 * - photos vs profile_photos vs main_photo_url vs avatar_url
 * - display_name vs name vs names[]
 * - video_intro_url vs video_url
 * 
 * This creates "Franken-profile" bugs where different UI components
 * read different fields and show inconsistent data.
 * 
 * Solution:
 * Run ALL profile data through this normalizer before rendering.
 * Guarantees consistent field names regardless of backend response shape.
 * 
 * Usage:
 *   import { normalizeProfile } from '../utils/normalizeProfile';
 *   const profile = normalizeProfile(apiResponse);
 *   // profile.display_name is always string
 *   // profile.profile_photos is always array of { url: string }
 *   // profile.photos is always array of strings
 *   // profile.video_intro_url is always string or null
 */

/**
 * Normalizes any profile object into a consistent shape.
 * 
 * @param {Object} p - Raw profile object from any backend endpoint
 * @returns {Object} Normalized profile with guaranteed field names
 * 
 * Guarantees:
 * - display_name: string (never null/undefined)
 * - profile_photos: Array<{ url: string }> (may be empty)
 * - photos: Array<string> (may be empty)
 * - video_intro_url: string | null
 */
export function normalizeProfile(p) {
  if (!p) return p;

  const profile = { ...p };

  // ─────────────────────────────────────────────────────────────
  // 1. DISPLAY NAME
  // Priority: display_name > name > names[].join(" & ") > fallback
  // ─────────────────────────────────────────────────────────────
  profile.display_name =
    profile.display_name ||
    profile.name ||
    (Array.isArray(profile.names) ? profile.names.filter(Boolean).join(" & ") : null) ||
    "Someone interesting";

  // ─────────────────────────────────────────────────────────────
  // 2. PHOTOS
  // Priority: profile_photos > photos > main_photo_url > avatar_url
  // Output: profile_photos = [{ url }], photos = [url strings]
  // ─────────────────────────────────────────────────────────────
  const fromProfilePhotos = Array.isArray(profile.profile_photos)
    ? profile.profile_photos
        .map((x) => (typeof x === "string" ? { url: x } : x))
        .filter((x) => x?.url)
    : [];

  const fromPhotos = Array.isArray(profile.photos)
    ? profile.photos
        .map((x) => (typeof x === "string" ? { url: x } : x))
        .filter((x) => x?.url)
    : [];

  const mainPhoto = profile.main_photo_url || profile.avatar_url || profile.photo_url;
  const mainAsObj = mainPhoto ? [{ url: mainPhoto }] : [];

  // Use first non-empty source
  profile.profile_photos =
    fromProfilePhotos.length ? fromProfilePhotos :
    fromPhotos.length ? fromPhotos :
    mainAsObj;

  // Also set photos array as strings for backwards compat
  profile.photos = profile.profile_photos.map((p) => p.url);

  // ─────────────────────────────────────────────────────────────
  // 3. VIDEO
  // Priority: video_intro_url > video_url
  // ─────────────────────────────────────────────────────────────
  profile.video_intro_url = profile.video_intro_url || profile.video_url || null;

  // ─────────────────────────────────────────────────────────────
  // 4. LOCATION (convenience normalization)
  // ─────────────────────────────────────────────────────────────
  if (!profile.location && (profile.location_city || profile.location_region)) {
    profile.location = [profile.location_city, profile.location_region]
      .filter(Boolean)
      .join(", ");
  }

  return profile;
}

/**
 * Extracts just the photo URLs from a profile.
 * Use this when you only need URLs, not the full normalized profile.
 * 
 * @param {Object} profile - Raw or normalized profile
 * @returns {Array<string>} Array of photo URL strings
 */
export function getPhotoUrls(profile) {
  if (!profile) return [];

  const rawPhotos = profile.photos || profile.profile_photos || [];
  if (!Array.isArray(rawPhotos)) return [];

  return rawPhotos
    .map((p) => {
      if (!p) return null;
      if (typeof p === "string") return p;
      if (typeof p === "object") {
        return p.url || p.src || p.image_url || p.path || null;
      }
      return null;
    })
    .filter(Boolean);
}

/**
 * Gets display name with couple handling.
 * 
 * @param {Object} profile - Raw or normalized profile
 * @returns {string} Display name
 */
export function getDisplayName(profile) {
  if (!profile) return "Someone interesting";
  
  // If couple with names array
  if (profile.gender === 'couple' && Array.isArray(profile.names) && profile.names.length > 0) {
    const validNames = profile.names.filter(n => n && n.trim());
    if (validNames.length === 2) {
      return `${validNames[0]} & ${validNames[1]}`;
    } else if (validNames.length === 1) {
      return validNames[0];
    }
  }
  
  return profile.display_name || profile.name || "Someone interesting";
}
